import { DateTime } from 'luxon';
import { randomId } from 'ui/Hooks';

import { buildElement, buildPendoAnnouncement, getContentButtons } from './buildContent';
import { DOMJSON, Guide, Module, ResourceCenter } from './pendoAnnouncments.types';
import { traverseAndGroupByType } from './utils/getGroupedTypes';

const pendoApi = window?.pendo;
type ProductLocations = 'ac' | 'db' | 'bs';
/**
 *
 * Provides an interface to interact with the pendo api
 *
 * This class is used to load the pendo resource center and it's modules
 * and to interact with the guides that are loaded
 */
class PendoAnnouncementApi {
  resourceCenter?: ResourceCenter;
  modules?: Module[];
  isPendoLoaded: boolean;
  isGuidesLoaded: boolean;
  /** Location of where the announcements are to be used, this is used to
   * retrieve the correct guides
   */
  productLocation?: ProductLocations;

  /** Called whenever guides are loaded or refreshed via the pendoApi */
  onGuidesLoaded?: (arg0: PendoAnnouncementApi) => void;

  constructor({
    onGuidesLoaded,
    productLocation,
  }: {
    onGuidesLoaded?: (arg0: PendoAnnouncementApi) => void;
    productLocation?: ProductLocations;
  }) {
    this.isPendoLoaded = false;
    this.isGuidesLoaded = false;

    // Start the pendo loading process
    this.onPendoLoaded();

    // Handle callback for guides loading
    this.onGuidesLoaded = onGuidesLoaded;

    this.resourceCenter =
      pendoApi?.BuildingBlocks?.BuildingBlockResourceCenter?.getResourceCenter();
    this.modules = this.resourceCenter?.modules;
    this.productLocation = productLocation;
  }

  /**
   *  Check if pendo is ready,  if it is ready calls load
   * otherwise attaches a callback to the event handler
   * */
  onPendoLoaded() {
    // Ensure pendo is ready before loading
    if (pendoApi?.isReady && pendoApi?.isReady()) {
      return this.loadPendo();
    } else {
      return pendoApi?.events?.ready?.(() => {
        return this.loadPendo();
      });
    }
  }

  /** Loads pendo if it is ready otherwise attaches a callback to the event handler */
  loadPendo() {
    this.isPendoLoaded = true;
    if (this.resourceCenter) {
      this.loadGuides();
    } else {
      const loadEvents = this.loadGuides.bind(this);
      pendoApi?.events?.guidesLoaded?.(() => loadEvents());
    }
  }

  /**
   *
   * Loads the resource center and it's modules then
   * then calls the onGuidesLoaded callback
   */
  loadGuides() {
    this.loadResourceCenter();
    this.isGuidesLoaded = true;

    if (this.onGuidesLoaded) {
      return this.onGuidesLoaded(this);
    }
  }

  /** Loads and assign the resource center and it's modules */
  loadResourceCenter() {
    this.resourceCenter = pendoApi?.BuildingBlocks.BuildingBlockResourceCenter.getResourceCenter();
    this.modules = this.resourceCenter?.modules;
  }

  get announcementModule() {
    return this.modules?.find((module) => module.name.toLowerCase() == 'announcements');
  }

  /** Gets all announcement guides that start with fema */
  get announcementModuleGuides() {
    return this.announcementModule?.guidesInModule.filter((guide) => {
      return (
        guide.isAnnouncement() &&
        guide.name.toLowerCase().startsWith('fema-' + this.productLocation)
      );
    });
  }

  get unseenAnnouncementGuides() {
    return this.announcementModuleGuides?.filter((guide) => !guide.hasBeenSeen());
  }
}

interface AnnouncementEvent {
  announcementId: string;
  announcementName: string;
  eventType:
    | 'announcementSeen'
    | 'announcementClosed'
    | 'announcementActivity'
    | 'announcementDismounted';
  seenDuration?: number;
  /**
   * Where was the announcement displayed from
   * This us useful when pendo and api announcements are mixed
   *
   */
  announcementOrigin: 'pendo' | 'api ';
  environment: 'production' | 'staging' | 'development';
  // Only used for 'announcementClicked'
  buttonText?: string;
}

interface TrackAnnouncement
  extends Pick<AnnouncementEvent, 'buttonText' | 'eventType' | 'seenDuration'> {
  guide: Guide;
}

const trackAnnouncement = ({ guide, eventType, buttonText, seenDuration }: TrackAnnouncement) => {
  const nonDevEnvironments =
    import.meta.env.VITE_API_ENV === 'production' ? 'production' : 'staging';

  // eslint-disable-next-line turbo/no-undeclared-env-vars
  const trackingEnv = import.meta.env.DEV ? 'development' : nonDevEnvironments;
  const announcementEvent: AnnouncementEvent = {
    announcementId: guide.id,
    announcementName: guide.name,
    eventType,
    announcementOrigin: 'pendo',
    environment: trackingEnv,
    seenDuration: seenDuration,
  };
  if (buttonText) announcementEvent.buttonText = buttonText;
  return pendoApi?.track('announcement', announcementEvent);
};

/**
 * Pendo Announcement class
 *
 * Used for each individual announcement
 * that is returned from the PendoAnnouncementApi
 */
class PendoAnnouncement {
  guide: Guide;
  /**  */
  isSeen: boolean;
  /** Guide used for our announcement  */
  step: Guide['steps'][0];

  seenStartTime: DateTime;

  domJsonUrl: string;

  /** Has the announcement been seen during this render? */
  seenTracked = false;

  _domJson?: DOMJSON;

  constructor(guide: Guide) {
    this.guide = guide;
    this.seenStartTime = DateTime.now();
    this.isSeen = guide.hasBeenSeen();
    this.step = guide.steps[0];
    this.domJsonUrl = this.step.domUrl;
    this._domJson = this.step.domJson;
  }

  get domJson(): DOMJSON | null {
    if (!this._domJson) {
      return null;
    }
    return this._domJson;
  }

  set domJson(json: DOMJSON) {
    this._domJson = json;
  }

  get elements() {
    if (!this.domJson) return null;
    return traverseAndGroupByType([this.domJson]);
  }

  get content() {
    if (!this.elements) return null;
    const content = buildPendoAnnouncement(this.elements);

    return {
      title: content?.title,
      children: content?.children,
      image: content?.image,
      buttons: this?.buttons,
    };
  }

  get title() {
    return this.content?.title;
  }

  get children() {
    return this.content?.children;
  }

  get image() {
    return this.content?.image;
  }

  get buttons() {
    return this.buildButtons();
  }

  get seenDuration() {
    const dismountTime = DateTime.now();
    const timeSeen = dismountTime.diff(this.seenStartTime, 'seconds').seconds;
    return timeSeen;
  }

  track({
    eventType,
    buttonText,
    seenDuration,
  }: Pick<TrackAnnouncement, 'eventType' | 'buttonText'> & { seenDuration?: number }) {
    return trackAnnouncement({ guide: this.guide, eventType, buttonText, seenDuration });
  }

  /**
   *
   * Gets the data  button content from the announcement
   * and builds out the jsx elements for the buttons
   */
  buildButtons() {
    if (!this.elements) return null;
    const buttons = getContentButtons(this.elements);

    const buttonsContent = buttons
      .slice(0, 2)
      .map((button, idx) => {
        if (!button?.actions?.[0]) return null;

        const { action, eventType, parameters } = button.actions[0];

        const props = { primary: idx === 0, key: randomId() };

        if (eventType === 'click') {
          if (action === 'launchGuide') {
            const value = parameters[0].value;
            if (!value) return;
            props['onClick'] = () => {
              this.track({
                eventType: 'announcementActivity',
                seenDuration: this.seenDuration,
                buttonText: button.content,
              });
              this.showGuide(value);
            };
          } else if (action == 'openLink') {
            const targetParam = parameters.find((param) => param.name === 'target');
            const value = parameters[0].value;
            if (!value) return;
            props['onClick'] = () => {
              this.track({
                eventType: 'announcementActivity',
                seenDuration: this.seenDuration,
                buttonText: button.content,
              });

              window.open(value, targetParam?.value);
            };
          }
        }
        return buildElement(button, 'button', props);
      })
      .reverse();
    return buttonsContent;
  }

  /** Shows a guide by id via pendo */
  showGuide(guideId: string) {
    pendoApi?.showGuideById(guideId);
  }

  /**
   * Marks the guide as seen
   *
   * Under the hood, this is done by advancing the step guide through the pendoApi
   * Attempts to call the pendoApi to load the guides again
   *  */
  close() {
    this.guide.steps[0].advance();
    this.track({ eventType: 'announcementClosed', seenDuration: this.seenDuration });
    pendoApi?.loadGuides();
  }
  /**
   *  Tracks when the component dismounts, this is used to send a track event of announcementDismounted to pendo
   *
   *  with the duration the announcement was mounted
   */
  dismount() {
    // Track the time the announcement was seen
    if (this.seenDuration > 3) {
      this.track({ eventType: 'announcementDismounted', seenDuration: this.seenDuration });
    }
    return this.seenDuration;
  }

  seen() {
    if (!this.seenTracked) {
      this.track({ eventType: 'announcementSeen' });
    }
    this.seenTracked = true;
  }
}

export { PendoAnnouncement, PendoAnnouncementApi };
